/**
 * Single fetch execution path for API client — SSRF guard, response mapping,
 * 429 auto-retry orchestration, session/payment window events.
 * Load-bearing: same branches and return shapes as pre-split `api-client.ts`.
 */

import { logIntentionalNonBlockingRejection } from "@/lib/monitoring/log-intentional-non-blocking-rejection";
import { assertSafeHttpUrlForServerFetch } from "@/lib/security/ssrf-url-guard";
import { clearTimeoutController } from "@/lib/config/api-timeouts";
import type { createTimeoutController } from "@/lib/config/api-timeouts";
import { logger } from "../../utils/logger";
import {
  buildFailureResponse,
  buildSuccessResponse,
  createNetworkErrorResponse,
  createTimeoutResponse,
  extractRequestId,
  handleRateLimitResponse,
  parseRetryAfter,
} from "../api-client-helpers";
import type { ApiClientConfig, ApiResult, RequestOptions } from "../api-client-types";
import { dispatchSubscriptionStateFromResponse } from "../subscription-state-headers";
import { mapPaymentRequiredFailure } from "../payment-required";

export type RequestOptionsInternal<TBody = unknown> = RequestOptions<TBody> & {
  _retryCount429?: number;
};

export async function executeRequest<T>(
  baseUrl: string,
  config: ApiClientConfig,
  url: string,
  init: RequestInit & { signal?: AbortSignal | null; body?: string | null },
  timeoutController: ReturnType<typeof createTimeoutController> | null,
  path: string,
  options: RequestOptionsInternal,
  on429Retry: (p: string, o: RequestOptionsInternal) => Promise<ApiResult<T>>,
): Promise<ApiResult<T>> {
  try {
    logger.info("[ApiClient] Making fetch request", {
      url,
      method: init.method,
      hasBody: !!init.body,
      bodyLength: init.body ? String(init.body).length : 0,
      hasSignal: !!init.signal,
    });

    if (url.startsWith("http://") || url.startsWith("https://")) {
      assertSafeHttpUrlForServerFetch(url, {
        relaxOutboundHostnameAllowlist: true,
      });
    } else if (baseUrl) {
      try {
        const base = new URL(baseUrl);
        const absoluteUrl = new URL(url, base).href;
        const host = base.hostname.toLowerCase();
        assertSafeHttpUrlForServerFetch(absoluteUrl, {
          extraAllowedHostnames: [host],
        });
      } catch {
        // baseUrl not a valid absolute URL — skip SSRF guard
      }
    }

    const res = await fetch(url, init);

    logger.info("[ApiClient] Fetch response received", {
      url,
      status: res.status,
      statusText: res.statusText,
      ok: res.ok,
    });

    if (timeoutController) {
      clearTimeoutController(timeoutController);
    }

    const requestId = extractRequestId(res);
    const retryAfterSeconds = parseRetryAfter(res.headers.get("Retry-After"));

    dispatchSubscriptionStateFromResponse(res);

    if (res.ok) {
      return buildSuccessResponse<T>(res, requestId);
    }

    const failure = await buildFailureResponse(
      res,
      requestId,
      retryAfterSeconds,
    );

    // Notify rate-limit listeners for 429s
    if (res.status === 429) {
      handleRateLimitResponse(res, requestId, retryAfterSeconds);
    }

    // Notify session-expired listeners for 401s
    if (res.status === 401 && typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("session-expired"));
    }

    // Notify subscription-inactive listeners only for true subscription blocks.
    // 402 is reused for budget/quota failures, which should not open the
    // "Subscription Inactive" overlay.
    if (res.status === 402 && typeof window !== "undefined") {
      const paymentRequired = mapPaymentRequiredFailure({
        code: failure.code,
        message: failure.message,
        status: failure.status,
      });

      window.dispatchEvent(
        new CustomEvent("payment-required", {
          detail: paymentRequired,
        }),
      );

      if (paymentRequired.shouldBlockApp) {
        window.dispatchEvent(new CustomEvent("subscription-inactive"));
      } else {
        window.dispatchEvent(
          new CustomEvent("usage-limit-exceeded", {
            detail: paymentRequired,
          }),
        );
      }
    }

    // Optional auto retry for 429 with max retry limit to prevent infinite loops
    const MAX_429_RETRIES = 3;
    const currentRetryCount = options._retryCount429 ?? 0;

    if (
      config.autoRetry429 &&
      res.status === 429 &&
      retryAfterSeconds &&
      retryAfterSeconds <= 5 &&
      currentRetryCount < MAX_429_RETRIES
    ) {
      if (options.signal?.aborted) {
        return failure;
      }

      logger.warn("[ApiClient] Rate limited (429), retrying after delay", {
        path,
        retryAfterSeconds,
        retryAttempt: currentRetryCount + 1,
        maxRetries: MAX_429_RETRIES,
      });
      await new Promise<void>((resolve, reject) => {
        const timeoutId = setTimeout(resolve, retryAfterSeconds * 1000);
        const abortHandler = () => {
          clearTimeout(timeoutId);
          reject(new DOMException("Aborted", "AbortError"));
        };

        options.signal?.addEventListener("abort", abortHandler, {
          once: true,
        });
      }).catch((err: unknown) => {
        if (
          err instanceof DOMException &&
          err.name === "AbortError"
        ) {
          return undefined;
        }
        logIntentionalNonBlockingRejection({
          operation: "API_CLIENT_429_RETRY_SLEEP",
          surface: "api_client",
          err,
        });
        return undefined;
      });

      if (options.signal?.aborted) {
        return failure;
      }

      return on429Retry(path, {
        ...options,
        _retryCount429: currentRetryCount + 1,
      });
    }

    // Log when max retries exceeded
    if (res.status === 429 && currentRetryCount >= MAX_429_RETRIES) {
      logger.error("[ApiClient] Max 429 retries exceeded, giving up", {
        path,
        retryAttempts: currentRetryCount,
        maxRetries: MAX_429_RETRIES,
      });
    }

    return failure;
  } catch (error) {
    if (timeoutController) {
      clearTimeoutController(timeoutController);
    }

    // Handle timeout/abort errors
    if (
      error instanceof Error &&
      (error.name === "AbortError" || error.message.includes("timeout"))
    ) {
      return createTimeoutResponse();
    }

    // Handle network errors
    if (error instanceof TypeError && error.message.includes("fetch")) {
      return createNetworkErrorResponse();
    }

    throw error;
  }
}
